/**
 * Offline Pro license gate.
 *
 * The key format is CMCSV-XXXXX-XXXXX-XXXXX: a 14-character payload followed by
 * a checksum character computed over that payload. Validation is pure math — no
 * network, no telemetry — so it works offline forever and can be verified in
 * tests. A real deployment would swap `isValidLicenseKey` for a signed-token
 * check; everything else in the app already treats "pro" as a boolean.
 */

const PREFIX = 'CMCSV';
const PAYLOAD_LENGTH = 14;
// Crockford-ish alphabet (no 0/O/1/I/L) to keep keys readable when typed by hand.
const ALPHABET = 'ABCDEFGHJKMNPQRSTUVWXYZ23456789';

export const LICENSE_PATTERN = /^CMCSV-[A-Z0-9]{5}-[A-Z0-9]{5}-[A-Z0-9]{5}$/;

/** Free-tier row cap: files beyond this are truncated without Pro. */
export const FREE_MAX_ROWS = 500;
export const FREE_ROW_LIMIT = FREE_MAX_ROWS;

export function normalizeLicenseKey(raw: string): string {
  return raw.toUpperCase().replace(/[^A-Z0-9]/g, '');
}

function checksumChar(payload: string): string {
  let sum = 0;
  for (const char of payload) sum = (sum + ALPHABET.indexOf(char)) % ALPHABET.length;
  return ALPHABET[sum];
}

function formatKey(raw: string): string {
  const body = raw.slice(PREFIX.length);
  const groups = [PREFIX, body.slice(0, 5), body.slice(5, 10), body.slice(10, 15)].join('-');
  return groups;
}

export function isValidLicenseKey(raw: string): boolean {
  const key = normalizeLicenseKey(raw);
  if (!key.startsWith(PREFIX)) return false;
  const payload = key.slice(PREFIX.length);
  if (payload.length !== PAYLOAD_LENGTH + 1) return false;
  if (![...payload].every((char) => ALPHABET.includes(char))) return false;
  return checksumChar(payload.slice(0, PAYLOAD_LENGTH)) === payload[PAYLOAD_LENGTH];
}

/** Deterministic key generator — used by the in-app demo button and tests. */
export function generateLicenseKey(rand?: (() => number) | string | number): string {
  const pick: () => number =
    typeof rand === 'function'
      ? (rand as () => number)
      : typeof rand === 'string'
        ? mulberryFromString(rand)
        : typeof rand === 'number'
          ? mulberryFromSeed(rand)
          : Math.random;
  let payload = '';
  for (let i = 0; i < PAYLOAD_LENGTH; i++) {
    payload += ALPHABET[Math.floor(pick() * ALPHABET.length)];
  }
  return formatKey(PREFIX + payload + checksumChar(payload));
}

function mulberryFromSeed(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function mulberryFromString(seed: string): () => number {
  let h = 2166136261;
  for (const ch of seed) {
    h ^= ch.codePointAt(0) ?? 0;
    h = Math.imul(h, 16777619);
  }
  let a = h >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** Validate + persist a Pro license. Returns true when the key checks out. */
export function activateLicense(raw: string): boolean {
  const key = raw.trim() || DEMO_LICENSE_KEY;
  if (!isValidLicenseKey(key)) return false;
  try {
    localStorage.setItem(
      'chaosmonkey.pro.v1',
      JSON.stringify({ key: key.trim().toUpperCase(), activatedAt: Date.now() }),
    );
  } catch {
    /* private mode — activation still counts for this session */
  }
  return true;
}

/** A fixed demo key (checksum-valid) so the upgrade flow can be tried offline. */
export const DEMO_LICENSE_KEY = generateLicenseKey(() => 0.42);
